'use client'

import { useEffect, useState, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { HamburgerMenu } from './HamburgerMenu'
import { MobileL1Nav } from './MobileL1Nav'
import { DesktopNav } from './DesktopNav'
import { MobileNav } from './MobileNav'
import { CtaDropdown } from './CtaDropdown'
import { getCdnUrl } from './navUtils'
import type { LinkCompType } from './navUtils'

export type L1Keys = string
export interface CtaItem {
  url: string
  label: string
  labelShort?: string
  type: string
}
export interface MenuType {
  label?: string
  items?: MenuType[]
  url?: string
  component?: { id: string }
  cta?: CtaItem[]
  columns?: number
}
export type MenuJsonType = Record<string, MenuType>

export default function Menu({
  L1Key,
  devBarDisabled = true,
  menuJson,
  LinkComp,
  currentPath,
  locale,
}: {
  L1Key?: string
  devBarDisabled?: boolean
  menuJson: MenuJsonType
  LinkComp: LinkCompType
  currentPath?: string
  locale?: string
}) {
  const pathname = usePathname()
  let currentMenu = (currentPath ?? pathname).split('/')[2] ?? ''
  currentMenu = Object.keys(menuJson).includes(currentMenu)
    ? currentMenu
    : 'parents'
  L1Key = L1Key ?? currentMenu

  // hamburger menu
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  // hide / show L1 nav (mobile)
  const [mobileScrolledDown, setMobileScrolledDown] = useState(false)
  // primary CTA dropdown
  const [ctaDropdownOpen, setCtaDropdownOpen] = useState(false)
  const [jsReady, setJsReady] = useState(false)
  const primaryCtaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setJsReady(true)
  }, [])

  useEffect(() => {
    if (!ctaDropdownOpen) return
    function handleOutsideClick(e: MouseEvent) {
      if (
        primaryCtaRef.current &&
        !primaryCtaRef.current.contains(e.target as Node)
      ) {
        setCtaDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [ctaDropdownOpen])

  //lock main content scroll if mobile menu open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [mobileNavOpen])

  const coreNavHeight = locale == 'en-AU' ? 62 : 50

  return (
    <nav
      className="font-display new-nav sticky top-0 z-50 bg-white font-light"
      data-l1-active-key={L1Key}
    >
      {/* L1 navigation - mobile */}
      <MobileL1Nav
        menuJson={menuJson}
        mobileNavOpen={mobileNavOpen}
        mobileScrolledDown={mobileScrolledDown}
        setMobileScrolledDown={setMobileScrolledDown}
        LinkComp={LinkComp}
      />
      <div className="relative z-20 border-b-2 border-black/15 bg-white lg:border-b-0">
        <div className="mx-auto h-full w-[1440px] max-w-full px-4 lg:px-8">
          <div
            className="flex"
            style={{ height: `${coreNavHeight.toString()}px` }}
          >
            {/* logo */}
            {locale == 'en-AU' ? (
              <LinkComp
                href="/"
                className="shrink-0 self-center"
                style={{ width: '56px' }}
              >
                <img
                  src={getCdnUrl('logos/re-logo-abc-nav-56.png')}
                  alt="ABC Reading Eggs"
                  width={56}
                  height={51}
                />
              </LinkComp>
            ) : (
              <LinkComp
                href="/"
                className="shrink-0 self-center"
                style={{ width: '60px' }}
              >
                <img
                  src={getCdnUrl('logos/re-logo-nav.png')}
                  alt="Reading Eggs"
                  width={60}
                  height={38}
                />
              </LinkComp>
            )}

            <div className="pr-2 md:pr-8"></div>
            <ul className="flex w-full gap-0 lg:gap-4">
              {/* navigation - desktop */}
              <DesktopNav
                menuJson={menuJson}
                L1Key={L1Key}
                LinkComp={LinkComp}
              />
              {/* CTA buttons */}
              <li className="grow"></li>
              <li className="flex items-center gap-1">
                {menuJson[L1Key]?.cta?.map((cta) =>
                  cta.type === 'primary' ? (
                    <div
                      key="primary-cta"
                      ref={primaryCtaRef}
                      className="group relative"
                    >
                      <button
                        onClick={() => {
                          setCtaDropdownOpen((prev) => !prev)
                        }}
                        className="nav-cta nav-cta-signup cursor-pointer"
                        data-trigger="cta-dropdown"
                        data-cta-type={cta.type}
                      >
                        {cta.labelShort ? (
                          <>
                            <span className="hidden sm:!block">
                              {cta.label}
                            </span>
                            <span className="block sm:hidden">
                              {cta.labelShort}
                            </span>
                          </>
                        ) : (
                          <span>{cta.label}</span>
                        )}
                        <span className="nav-chevron ml-1 translate-y-[1px]" />
                      </button>
                      <div
                        data-cta-dropdown
                        className={
                          ctaDropdownOpen
                            ? 'block'
                            : jsReady
                              ? 'hidden'
                              : 'hidden group-hover:block'
                        }
                      >
                        <CtaDropdown LinkComp={LinkComp} />
                      </div>
                    </div>
                  ) : (
                    <LinkComp
                      key={cta.url}
                      href={cta.url}
                      className="nav-cta nav-cta-login"
                      data-cta-type={cta.type}
                    >
                      {cta.labelShort ? (
                        <>
                          <span className="hidden sm:!block">{cta.label}</span>
                          <span className="block sm:hidden">
                            {cta.labelShort}
                          </span>
                        </>
                      ) : (
                        <span>{cta.label}</span>
                      )}
                    </LinkComp>
                  ),
                )}
                {/* hamburger menu - mobile */}
                <HamburgerMenu
                  isOpen={mobileNavOpen}
                  setIsOpen={setMobileNavOpen}
                  className="block lg:hidden"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* spacer - desktop */}
      <div className="hidden h-[40px] lg:!block"></div>
      {/* navigation - mobile */}
      <MobileNav
        menu={menuJson[L1Key] ?? {}}
        L1Key={L1Key}
        isOpen={mobileNavOpen}
        devBarDisabled={devBarDisabled}
        LinkComp={LinkComp}
        locale={locale}
      />
    </nav>
  )
}
